import api from '../axiosConfig';
import { OTA } from '../config/apiEndpoints';

export const OtaService = {
    /**
     * List Connected OTA Channels
     * Flat aggregator, no pagination/sorting allowed.
     * `x-tenant-id` header is automatically attached via the axios interceptor.
     * 
     * @returns {Promise<Array>} Array of OTA channel objects
     */
    getConnectedChannels: async () => {
        try {
            const response = await api.get(OTA.CHANNELS);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('OtaService getConnectedChannels error:', error);
            throw error;
        }
    },

    /**
     * Connect New OTA Channel
     * POST payload conforming to strict channel integration schema.
     * 
     * @param {Object} payload Configuration mappings object
     * @returns {Promise<Object>} The connected channel response
     */
    connectChannel: async (payload) => {
        try {
            const response = await api.post(OTA.CHANNELS, payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('OtaService connectChannel error:', error);
            throw error;
        }
    },

    /**
     * View OTA Bookings Log
     * Fetch paginated log history filtered by channel and status.
     * `x-tenant-id` header is automatically attached via the axios interceptor.
     * 
     * @param {Object} params - Query parameters (channel, status, page, limit)
     * @returns {Promise<Object>} Paginated log response { data, meta }
     */
    getBookingsLog: async (params = {}) => {
        try {
            const axiosParams = {};
            if (params.channel) axiosParams.channel = params.channel;
            if (params.status && params.status !== 'All') axiosParams.status = params.status;
            if (params.page !== undefined) axiosParams.page = params.page + 1; // Backend typically expects 1-indexed, frontend UI is 0-indexed
            if (params.limit) axiosParams.limit = params.limit;

            const response = await api.get(OTA.BOOKINGS_LOG, { params: axiosParams });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('OtaService getBookingsLog error:', error);
            throw error;
        }
    },

    /**
     * Get OTA Channel Details
     * Fetch purely informational configuration mappings for a specific channel.
     * `x-tenant-id` header is automatically attached via the axios interceptor.
     * 
     * @param {string} id - The unique channel integration ID
     * @returns {Promise<Object>} Channel details response object
     */
    getChannelDetails: async (id) => {
        try {
            const response = await api.get(OTA.CHANNEL_BY_ID(id));
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('OtaService getChannelDetails error:', error);
            throw error;
        }
    },

    /**
     * Disconnect OTA Channel
     * Deletes a third-party platform configuration.
     * `x-tenant-id` header is automatically attached via the axios interceptor.
     * 
     * @param {string} id - The unique channel integration ID
     * @returns {Promise<Object>} Success confirmation response
     */
    disconnectChannel: async (id) => {
        try {
            const response = await api.delete(OTA.CHANNEL_BY_ID(id));
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('OtaService disconnectChannel error:', error);
            throw error;
        }
    },

    /**
     * Update OTA Room Type Mapping
     * Replaces the array of room mappings for a given OTA channel.
     * `x-tenant-id` header is automatically attached via the axios interceptor.
     * 
     * @param {string} id - The unique channel integration ID
     * @param {Object} payload - Must match { roomMappings: [...] } exactly
     * @returns {Promise<Object>} The updated mapping response
     */
    updateRoomMapping: async (id, payload) => {
        try {
            const response = await api.put(OTA.UPDATE_ROOM_MAPPING(id), payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('OtaService updateRoomMapping error:', error);
            throw error;
        }
    },

    /**
     * Update OTA Rate Mapping
     * Replaces the array of rate mappings for a given OTA channel.
     * `x-tenant-id` header is automatically attached via the axios interceptor.
     * 
     * @param {string} id - The unique channel integration ID
     * @param {Object} payload - Must match { rateMappings: [...] } exactly
     * @returns {Promise<Object>} The updated mapping response
     */
    updateRateMapping: async (id, payload) => {
        try {
            const response = await api.put(OTA.UPDATE_RATE_MAPPING(id), payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('OtaService updateRateMapping error:', error);
            throw error;
        }
    },

    /**
     * Sync Availability and Rates
     * Force pushes an inventory and rate override to the OTA channel.
     * `x-tenant-id` header is automatically attached via the axios interceptor.
     * 
     * @param {string} id - The unique channel integration ID
     * @returns {Promise<Object>} Success confirmation response
     */
    syncChannel: async (id) => {
        try {
            const response = await api.post(OTA.SYNC_CHANNEL(id));
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('OtaService syncChannel error:', error);
            throw error;
        }
    },

    /**
     * Manage Stop-Sell
     * Blocks or opens availability on the OTA channel for specific dates.
     * `x-tenant-id` header is automatically attached via the axios interceptor.
     * 
     * @param {string} id - The unique channel integration ID
     * @param {Object} payload - { roomTypeId, startDate, endDate, stopSell }
     * @returns {Promise<Object>} Success confirmation response
     */
    manageStopSell: async (id, payload) => {
        try {
            const response = await api.post(OTA.STOP_SELL_CHANNEL(id), payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('OtaService manageStopSell error:', error);
            throw error;
        }
    },

    /**
     * Set Booking Restrictions
     * Defines structural stay conditions for specific dates on the OTA channel.
     * `x-tenant-id` header is automatically attached via the axios interceptor.
     * 
     * @param {string} id - The unique channel integration ID
     * @param {Object} payload - { roomTypeId, startDate, endDate, minStay, maxStay, closedToArrival, closedToDeparture }
     * @returns {Promise<Object>} Success confirmation response
     */
    setBookingRestrictions: async (id, payload) => {
        try {
            const response = await api.post(OTA.SET_RESTRICTIONS_CHANNEL(id), payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('OtaService setBookingRestrictions error:', error);
            throw error;
        }
    },

    /**
     * Retry Failed Booking Import
     * Re-triggers an ingestion attempt for a third-party channel booking that previously failed.
     * `x-tenant-id` header is automatically attached via the axios interceptor.
     * 
     * @param {string} id - The unique log record ID
     * @returns {Promise<Object>} Success confirmation response
     */
    retryBookingLog: async (id) => {
        try {
            const response = await api.post(OTA.RETRY_BOOKING_LOG(id));
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('OtaService retryBookingLog error:', error);
            throw error;
        }
    },

    /**
     * View Channel Performance Report
     * Fetches the flat performance report analytics for OTA channels.
     * `x-tenant-id` header is automatically attached via the axios interceptor.
     * 
     * @returns {Promise<Array>} Array of channel operational metrics
     */
    getPerformanceReport: async () => {
        try {
            const response = await api.get(OTA.CHANNEL_PERFORMANCE);
            return response.data?.data || response.data || [];
        } catch (error) {
            console.error('OtaService getPerformanceReport error:', error);
            throw error;
        }
    }
};
